import { useState, useEffect, useMemo } from 'react';
import Link from '@/lib/siteLink';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Clock, User, TrendingUp, Gamepad2, Trophy, BookOpen, ShoppingBag, Play, Sparkles, ChevronRight } from 'lucide-react';
import GamesNav from './GamesNav';
import GamesFooter from './GamesFooter';
import EventsFeed from './components/EventsFeed';
import LiveTicker from './components/LiveTicker';
import AffiliateLink from './utils/AffiliateLink';
import AdSlot from '@/components/AdComponents';
import { CATEGORIES } from './GamesNav';
import { GAMES } from './data/gamesLibrary';
import { GEAR } from './data/gearCatalog';
import { useGamesTheme } from './useGamesTheme';
import { stripTags, IMG_PLACEHOLDER, formatRelativeTime } from './utils/gamesFormat';
import { sized, sizedSrcSet } from '@/lib/imageSize';
import TrackedLink from '@/components/AffiliateLink';
import { topTopics, seenIds } from '@/lib/interest';

const API = process.env.REACT_APP_BACKEND_URL;

function ArticleCard({ article, featured = false }) {
  const rawImg = typeof article.featured_image === 'string' ? article.featured_image.trim() : '';
  const hasImg = rawImg && (rawImg.startsWith('http') || rawImg.startsWith('/'));
  const imgUrl = hasImg ? rawImg : IMG_PLACEHOLDER;
  const title = stripTags(article.title);
  const excerpt = stripTags(article.excerpt);

  return (
    <Link
      to={`/games/blog/${article.slug || article.id}`}
      className={`group ${featured ? 'md:col-span-2' : ''}`}
      data-testid={`article-${article.id}`}
    >
      <div className="bg-white dark:bg-[#1E293B] rounded-2xl overflow-hidden border border-slate-200 dark:border-white/5 hover:border-yellow-500/30 transition-all hover:shadow-lg hover:shadow-yellow-500/5 h-full flex flex-col">
        <div className="aspect-video bg-gray-800 overflow-hidden">
          <img
            src={sized(imgUrl, 640)}
            srcSet={sizedSrcSet(imgUrl)}
            sizes="(max-width: 640px) 100vw, 400px"
            alt={title}
            onError={(e) => { if (e.currentTarget.src !== IMG_PLACEHOLDER) { e.currentTarget.srcset = ''; e.currentTarget.src = IMG_PLACEHOLDER; } }}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="p-5 flex flex-col flex-1">
          {article.category && (
            <span className="text-[10px] font-bold text-amber-600 dark:text-yellow-400 uppercase tracking-widest mb-2">{article.category}</span>
          )}
          <h3 className={`font-bold text-slate-900 dark:text-white mb-2 group-hover:text-amber-600 dark:text-yellow-400 transition-colors line-clamp-2 ${featured ? 'text-xl' : 'text-sm'}`}>
            {title}
          </h3>
          {excerpt && <p className="text-sm text-slate-600 dark:text-gray-300 mb-3 line-clamp-2 flex-1">{excerpt}</p>}
          <div className="flex items-center gap-3 text-[11px] text-slate-500 dark:text-gray-400 mt-auto">
            {article.author && <span className="flex items-center gap-1"><User className="w-3 h-3" />{article.author}</span>}
            {(article.updated_at || article.published_at || article.created_at) && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatRelativeTime(article.updated_at || article.published_at || article.created_at)}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}

// Cricket + soccer lead the home page (owner priority, Sep 2026). Newer articles have no category, so fall back
// to keywords in the title/excerpt.
const SPORT_RE = {
  cricket: /\b(cricket|ipl|t20|odi|test match|ashes|bcci|ecb|the hundred|wicket|kohli|dhoni|rohit sharma|bumrah|world cup cricket|county championship)\b/i,
  soccer: /\b(soccer|football|premier league|epl|champions league|ucl|la liga|serie a|bundesliga|mls|uefa|fifa|messi|ronaldo|haaland|mbapp[eé]|arsenal|liverpool|manchester (united|city)|chelsea|real madrid|barcelona)\b/i,
};
function sportOf(a) {
  if (a.category === 'cricket' || a.category === 'soccer') return a.category;
  const text = `${a.title || ''} ${a.excerpt || ''}`;
  if (SPORT_RE.cricket.test(text)) return 'cricket';
  if (SPORT_RE.soccer.test(text)) return 'soccer';
  return null;
}

function SportColumn({ sport, label, items }) {
  return (
    <div>
      <div className="flex items-end justify-between mb-4">
        <h3 className="text-xl font-black text-slate-900 dark:text-white">{label}</h3>
        <Link to={`/games/${sport}`} className="text-xs font-bold text-amber-600 dark:text-yellow-400 flex items-center gap-1">More {label.toLowerCase()} <ArrowRight className="w-3 h-3" /></Link>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">{items.map((a) => <ArticleCard key={a.id} article={a} />)}</div>
    </div>
  );
}

function ForYouRail() {
  const [bundle, setBundle] = useState(null);
  const topics = useMemo(() => topTopics(6), []);
  useEffect(() => {
    if (!topics.length) return;
    const qs = new URLSearchParams({ site: 'games', topics: topics.join(','), seen: seenIds().join(','), limit: '4' });
    fetch(`${API}/api/recommend?${qs}`).then((r) => r.json()).then(setBundle).catch(() => {});
  }, [topics]);
  if (!topics.length || !bundle) return null;
  const { articles = [], products = [], games = [], build } = bundle;
  if (!articles.length && !products.length && !games.length) return null;

  return (
    <section className="py-14 border-t border-slate-200 dark:border-white/5" data-testid="home-foryou">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <span className="text-xs font-bold text-cyan-600 dark:text-cyan-300 uppercase tracking-widest">For you</span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
              Because you were reading <span className="text-cyan-600 dark:text-cyan-300">{topics.slice(0, 2).join(' & ')}</span>
            </h2>
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          {articles.length > 0 && (
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
              {articles.slice(0, 4).map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          )}
          <div className="space-y-4">
            {products.length > 0 && (
              <div className="bg-white dark:bg-[#1E293B] rounded-2xl border border-slate-200 dark:border-white/5 p-4">
                <p className="font-bold text-slate-900 dark:text-white text-sm mb-3">Gear for this</p>
                <ul className="space-y-3">
                  {products.slice(0, 3).map((p) => (
                    <li key={p.id} className="flex gap-3">
                      {p.image && <img src={sized(p.image, 320)} alt="" className="w-14 h-14 rounded-lg object-contain bg-slate-100 dark:bg-white/5" loading="lazy" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white line-clamp-2">{p.title}</p>
                        <p className="text-xs text-slate-500">{p.price ? `$${Math.round(p.price)}` : ''} · {p.why}</p>
                      </div>
                      <TrackedLink href={p.url} partner={(p.merchant || '').toLowerCase()} placement="home-foryou"
                        className="self-center text-xs font-bold text-amber-600 dark:text-yellow-400 whitespace-nowrap">Check ↗</TrackedLink>
                    </li>
                  ))}
                </ul>
                <Link to="/games/shop" className="inline-block mt-3 text-xs font-bold text-amber-600 dark:text-yellow-400">All picks →</Link>
              </div>
            )}
            {games.length > 0 && (
              <div className="bg-white dark:bg-[#1E293B] rounded-2xl border border-slate-200 dark:border-white/5 p-4">
                <p className="font-bold text-slate-900 dark:text-white text-sm mb-3">Take a break, play one</p>
                <ul className="space-y-2">
                  {games.map((g) => (
                    <li key={g.slug}>
                      <Link to={`/games/play/${g.slug}`} className="flex items-center gap-3 group">
                        {g.thumbnail && <img src={sized(g.thumbnail, 320)} alt="" className="w-12 h-12 rounded-lg object-cover" loading="lazy" />}
                        <span className="text-sm font-semibold text-slate-900 dark:text-white group-hover:text-emerald-500">{g.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {build && (
              <Link to={build.url} className="block bg-gradient-to-br from-cyan-500/10 to-transparent rounded-2xl border border-cyan-500/30 p-4">
                <p className="font-bold text-slate-900 dark:text-white text-sm">{build.cta}</p>
                <p className="text-xs text-slate-600 dark:text-gray-300 mt-1">Compatible parts, live prices, no guesswork.</p>
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const PILLARS = [
  { id: 'read',   icon: BookOpen,    title: 'Read',   tint: 'text-amber-600 dark:text-yellow-400',  bg: 'from-yellow-400/10', path: '/games/cricket', tagline: 'Cricket, gaming, esports, soccer, the stories fans care about.' },
  { id: 'shop',   icon: ShoppingBag, title: 'Shop',   tint: 'text-cyan-600 dark:text-cyan-300',    bg: 'from-cyan-300/10',   path: '/games/shop',    tagline: 'Hand-picked gear for PC, console, mobile + a free PC builder.' },
  { id: 'play',   icon: Play,        title: 'Play',   tint: 'text-emerald-600 dark:text-emerald-300', bg: 'from-emerald-300/10',path: '/games/play',    tagline: 'Free browser games. Curated. No downloads, no ads in your face.' },
  { id: 'create', icon: Sparkles,    title: 'Create', tint: 'text-purple-600 dark:text-purple-300',  bg: 'from-purple-300/10', path: '/games/create',  tagline: 'Soon: describe a game, AI builds it. Join the early waitlist.', hot: true },
];

export default function GamesHome() {
  const { theme, toggleTheme } = useGamesTheme();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sportPool, setSportPool] = useState([]);

  useEffect(() => {
    // Older categorised cricket/soccer articles, merged with keyword matches from the latest pool below
    Promise.all(['cricket', 'soccer'].map((c) =>
      fetch(`${API}/api/content/articles?site=games&status=published&lightweight=true&require_image=true&category=${c}&limit=4`)
        .then((r) => r.json()).then((d) => d.articles || []).catch(() => [])))
      .then((lists) => setSportPool(lists.flat()));
  }, []);

  useEffect(() => {
    fetch(`${API}/api/content/articles?site=games&status=published&lightweight=true&exclude_sponsored=true&require_image=true&limit=100`)
      .then((r) => r.json())
      .then((data) => setArticles((data.articles || []).filter((a) => a.featured_image)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const topGames = GAMES.filter(g => !g.ai_built).slice(0, 4);
  const topGear = GEAR.slice(0, 4);

  return (
    <div className={`${theme} min-h-screen ${theme === 'dark' ? 'bg-[#0F172A]' : 'bg-slate-50'}`}>
      <Helmet>
        <title>GamesNFans, Read · Shop · Play · Create</title>
        <meta name="description" content="The ultimate destination for gamers. Read about esports, cricket & gaming. Shop curated gear. Play browser games. Create your own with AI." />
        <meta property="og:title" content="GamesNFans, Read · Shop · Play · Create" />
        <meta property="og:description" content="Your destination for esports, gaming, gear and creator tools. The 4-pillar home of gamers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gamesnfans.tv" />
        <link rel="canonical" href="https://www.gamesnfans.tv" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "WebSite",
          "name": "GamesNFans", "url": "https://www.gamesnfans.tv",
          "description": "Read, shop, play and create games, all in one place.",
          "potentialAction": { "@type": "SearchAction", "target": "https://www.gamesnfans.tv/games?q={search_term_string}", "query-input": "required name=search_term_string" }
        })}</script>
      </Helmet>

      <GamesNav theme={theme} toggleTheme={toggleTheme} />
      <LiveTicker articles={articles} />

      {/* Hero with 4 pillars */}
      <section className="relative overflow-hidden" data-testid="games-hero">
        <div className="absolute inset-0 dark:bg-[radial-gradient(ellipse_at_top_left,_rgba(250,204,21,0.10),transparent_55%),radial-gradient(ellipse_at_bottom_right,_rgba(168,85,247,0.10),transparent_55%)]" />
        <div className="max-w-6xl mx-auto px-6 py-16 sm:py-20 relative">
          <div className="flex items-center gap-2 mb-5">
            <Gamepad2 className="w-4 h-4 text-amber-600 dark:text-yellow-400" />
            <span className="text-xs font-bold text-amber-600 dark:text-yellow-400 uppercase tracking-widest">GamesNFans · Games · Sports · Entertainment</span>
          </div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.05] mb-6 max-w-4xl">
            For people who play games <br />
            <span className={`bg-gradient-to-r ${theme === 'dark' ? 'from-yellow-300 via-emerald-300 to-purple-300' : 'from-amber-600 via-emerald-600 to-purple-600'} bg-clip-text text-transparent`}>like they mean it.</span>
          </h1>
          <p className="text-base sm:text-xl text-slate-600 dark:text-gray-300 max-w-2xl mb-10">
            Read the stories, shop the gear, play the games, build your own, without leaving a single tab.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="games-pillars">
            {PILLARS.map(p => (
              <Link
                key={p.id}
                to={p.path}
                className={`group relative overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 bg-gradient-to-br ${p.bg} to-transparent p-6 hover:border-slate-300 dark:border-white/30 hover:-translate-y-1 transition-all`}
                data-testid={`hero-pillar-${p.id}`}
              >
                <p.icon className={`w-7 h-7 ${p.tint} mb-4`} />
                <div className="flex items-center gap-2">
                  <h3 className="text-2xl font-black text-slate-900 dark:text-white">{p.title}</h3>
                  {p.hot && <span className="text-[9px] font-black bg-purple-500 text-slate-900 dark:text-white px-1.5 py-0.5 rounded uppercase">New</span>}
                </div>
                <p className="text-xs text-slate-600 dark:text-gray-300 mt-2 leading-relaxed">{p.tagline}</p>
                <ArrowRight className={`w-4 h-4 ${p.tint} mt-5 opacity-0 group-hover:opacity-100 transition-opacity`} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Watch & Play this week (fans-of-everything weekly calendar) */}
      <EventsFeed />

      <ForYouRail />

      {/* Cricket & soccer first */}
      {!loading && (() => {
        const pool = [...articles, ...sportPool].filter((a, i, arr) => arr.findIndex((b) => b.id === a.id) === i)
          .sort((x, y) => String(y.updated_at || '').localeCompare(String(x.updated_at || '')));
        const cricket = pool.filter((a) => sportOf(a) === 'cricket').slice(0, 4);
        const soccer = pool.filter((a) => sportOf(a) === 'soccer').slice(0, 4);
        if (!cricket.length && !soccer.length) return null;
        return (
          <section className="py-14 border-t border-slate-200 dark:border-white/5" data-testid="home-sports-section">
            <div className="max-w-6xl mx-auto px-6 grid gap-10 lg:grid-cols-2">
              {cricket.length > 0 && <SportColumn sport="cricket" label="Cricket" items={cricket} />}
              {soccer.length > 0 && <SportColumn sport="soccer" label="Soccer" items={soccer} />}
            </div>
          </section>
        );
      })()}

      {/* Read teaser */}
      <section className="py-14 border-t border-slate-200 dark:border-white/5" data-testid="home-read-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-amber-600 dark:text-yellow-400" />
                <span className="text-xs font-bold text-amber-600 dark:text-yellow-400 uppercase tracking-widest">Read</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">Trending stories</h2>
            </div>
            <Link to="/games/gaming" className="text-xs font-bold text-amber-600 dark:text-yellow-400 hover:text-amber-600 dark:text-yellow-300 flex items-center gap-1">All stories <ArrowRight className="w-3 h-3" /></Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-3 gap-5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white dark:bg-[#1E293B] rounded-2xl p-6 animate-pulse">
                  <div className="aspect-video bg-gray-700 rounded-lg mb-4" />
                  <div className="h-4 bg-gray-700 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-800 rounded w-full" />
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/games/${c.slug}`}
                  className="bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-white/5 rounded-xl p-5 text-center hover:border-yellow-500/30 transition-all group"
                  data-testid={`home-cat-${c.slug}`}
                >
                  <Trophy className="w-5 h-5 text-amber-600 dark:text-yellow-400 mx-auto mb-2" />
                  <h3 className="font-bold text-slate-900 dark:text-white text-sm group-hover:text-amber-600 dark:text-yellow-400 transition-colors">{c.label}</h3>
                </Link>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
              {articles.slice(0, 6).map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          )}
        </div>
      </section>

      {/* Shop teaser */}
      <section className="py-14 border-t border-slate-200 dark:border-white/5" data-testid="home-shop-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <ShoppingBag className="w-4 h-4 text-cyan-600 dark:text-cyan-300" />
                <span className="text-xs font-bold text-cyan-600 dark:text-cyan-300 uppercase tracking-widest">Shop</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">Gear that wins</h2>
            </div>
            <Link to="/games/shop" className="text-xs font-bold text-cyan-600 dark:text-cyan-300 hover:text-slate-900 dark:text-white flex items-center gap-1">Open Shop <ArrowRight className="w-3 h-3" /></Link>
          </div>

          <div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {topGear.map(g => (
                <AffiliateLink key={g.id} href={g.affiliate_url} retailer="amazon" productId={g.id} productName={g.title} className="group bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-white/5 rounded-xl overflow-hidden hover:border-cyan-300/30 transition-all" testid={`home-gear-${g.id}`}>
                  <div className="aspect-square bg-gray-800 overflow-hidden">
                    <img src={g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] font-bold text-amber-600 dark:text-yellow-400 uppercase tracking-widest">{g.brand}</div>
                    <div className="text-xs font-bold text-slate-900 dark:text-white line-clamp-2 mt-0.5">{g.title}</div>
                    <div className="text-xs text-cyan-600 dark:text-cyan-300 mt-1">${g.price_usd}</div>
                  </div>
                </AffiliateLink>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Play teaser */}
      <section className="py-14 border-t border-slate-200 dark:border-white/5" data-testid="home-play-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Play className="w-4 h-4 text-emerald-600 dark:text-emerald-300" />
                <span className="text-xs font-bold text-emerald-600 dark:text-emerald-300 uppercase tracking-widest">Play</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">Free browser arcade</h2>
            </div>
            <Link to="/games/play" className="text-xs font-bold text-emerald-600 dark:text-emerald-300 hover:text-slate-900 dark:text-white flex items-center gap-1">Open Arcade <ArrowRight className="w-3 h-3" /></Link>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {topGames.map(g => (
              <Link key={g.slug} to={`/games/play/${g.slug}`} className="group bg-white dark:bg-[#1E293B] border border-slate-200 dark:border-white/5 rounded-2xl overflow-hidden hover:border-emerald-300/30 transition-all" data-testid={`home-game-${g.slug}`}>
                <div className="aspect-video bg-gradient-to-br from-purple-900/40 to-emerald-500/10 overflow-hidden relative">
                  <img
                    src={g.thumbnail}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/50 dark:from-[#0F172A]/50 to-transparent pointer-events-none" />
                </div>
                <div className="p-3">
                  <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-300 uppercase tracking-widest">{g.category}</div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5 group-hover:text-emerald-600 dark:text-emerald-300 transition-colors line-clamp-1">{g.title}</h3>
                  <div className="text-[11px] text-slate-600 dark:text-gray-300 mt-1">{(g.plays / 1000).toFixed(0)}k plays</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Create CTA strip */}
      <section className="py-14 border-t border-slate-200 dark:border-white/5" data-testid="home-create-section">
        <div className="max-w-6xl mx-auto px-6">
          <Link to="/games/create" className="group relative overflow-hidden rounded-3xl border border-purple-500/30 bg-gradient-to-r from-purple-900/40 via-fuchsia-900/30 to-slate-50 dark:to-[#0F172A] p-8 sm:p-12 block">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_right,_rgba(168,85,247,0.20),transparent_60%)]" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-purple-500/15 border border-purple-500/30 px-3 py-1 rounded-full mb-5">
                <Sparkles className="w-3.5 h-3.5 text-purple-600 dark:text-purple-300" />
                <span className="text-[11px] font-bold text-purple-700 dark:text-purple-200 uppercase tracking-widest">Create · Coming Q2</span>
              </div>
              <h2 className="text-3xl sm:text-5xl font-black text-slate-900 dark:text-white tracking-tight mb-3 max-w-3xl">
                Describe a game. Get a playable game.
              </h2>
              <p className="text-base sm:text-lg text-slate-600 dark:text-gray-300 max-w-xl mb-6">The AI Game Builder is on its way. Join 2,000+ creators on the waitlist for free Pro credits at launch.</p>
              <span className="inline-flex items-center gap-2 bg-purple-500 group-hover:bg-purple-400 text-slate-900 dark:text-white font-bold px-6 py-3 rounded-xl text-sm" data-testid="home-create-cta">
                Join the waitlist <ChevronRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8" data-testid="games-ad-slot">
        <div className="max-w-6xl mx-auto px-6">
          <AdSlot site="games" placement="home_bottom" maxAds={1} />
        </div>
      </section>

      <GamesFooter />
    </div>
  );
}

export { ArticleCard };
